import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, BACKEND_URL } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const labelCls = "label-eyebrow text-[#555]";
const inputCls =
  "mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]";

/**
 * Admin-side article amender. Loads the full article (incl. body) via
 * GET /articles/{id} and PATCHes any edited subset of fields.
 *
 * Used from /admin/articles to amend a published or pending article
 * without sending it back through the writer dashboard.
 */
export const AdminArticleEditDialog = ({ articleId, open, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  useEffect(() => {
    if (!articleId || !open) {
      setForm(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/articles/${articleId}`);
        setForm({
          title: data.title || "",
          slug: data.slug || "",
          excerpt: data.excerpt || "",
          body: data.body || "",
          cover_image: data.cover_image || "",
          gallery: Array.isArray(data.gallery) ? data.gallery : [],
          video_url: data.video_url || "",
          status: data.status,
        });
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load article");
        onClose?.();
      }
    })();
  }, [articleId, open, onClose]);

  if (!articleId || !open || !form) return null;

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const uploadImage = async (file) => {
    const data = new FormData();
    data.append("file", file);
    const { data: r } = await api.post("/upload", data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.url;
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, cover_image: url }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const f of files) {
        urls.push(await uploadImage(f));
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 20) }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingGallery(false);
      e.target.value = "";
    }
  };

  const removeFromGallery = (idx) => {
    setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug,
        excerpt: form.excerpt,
        body: form.body,
        cover_image: form.cover_image,
        gallery: form.gallery,
        video_url: form.video_url,
      };
      const { data } = await api.patch(`/articles/${articleId}`, payload);
      toast.success(
        data.status === "published"
          ? "Published article amended — changes are live"
          : "Article saved",
      );
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isPublished = form.status === "published";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="admin-article-edit-dialog"
        className="rounded-none border border-[#E5E0D8] bg-white max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="font-serif-display text-3xl text-[#111] font-light">
                Amend article
              </DialogTitle>
              <DialogDescription className="text-sm text-[#555]">
                {isPublished
                  ? "Changes go live immediately — no re-review required."
                  : "This article is still in draft / pending review."}
              </DialogDescription>
            </div>
            <span
              className={`label-eyebrow px-2 py-1 border ${
                isPublished
                  ? "bg-[#E8F5E9] text-[#1B5E20] border-[#2E7D32]/40"
                  : "bg-[#FFF8E1] text-[#A87900] border-[#A87900]/40"
              }`}
              data-testid="article-status-pill"
            >
              {form.status}
            </span>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div>
            <label className={labelCls}>Title</label>
            <Input
              data-testid="article-edit-title"
              required
              value={form.title}
              onChange={set("title")}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Slug</label>
            <Input
              data-testid="article-edit-slug"
              value={form.slug}
              onChange={set("slug")}
              placeholder="auto if blank"
              className={inputCls}
            />
            {isPublished && form.slug && (
              <p className="text-xs text-[#A87900] mt-1">
                Heads up — changing the slug breaks the existing public URL.
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Excerpt · 1-2 lines for previews</label>
            <Textarea
              data-testid="article-edit-excerpt"
              rows={2}
              value={form.excerpt}
              onChange={set("excerpt")}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>
          <div>
            <label className={labelCls}>Body · supports paragraphs separated by blank lines</label>
            <Textarea
              data-testid="article-edit-body"
              rows={14}
              value={form.body}
              onChange={set("body")}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111] font-mono text-sm"
            />
          </div>

          <div>
            <label className={labelCls}>Cover image</label>
            <div className="mt-2 flex items-center gap-4">
              {form.cover_image ? (
                <img
                  src={resolveImg(form.cover_image)}
                  alt=""
                  className="w-32 h-20 object-cover border border-[#E5E0D8] flex-shrink-0"
                />
              ) : (
                <div className="w-32 h-20 bg-[#F4EFE6] border border-[#E5E0D8] flex-shrink-0" />
              )}
              <div className="flex-1">
                <input
                  data-testid="article-edit-cover-file"
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  disabled={uploadingCover}
                  className="text-xs"
                />
                {uploadingCover && <span className="ml-2 label-eyebrow text-[#A87900]">Uploading…</span>}
              </div>
            </div>
            <Input
              data-testid="article-edit-cover-url"
              value={form.cover_image}
              onChange={set("cover_image")}
              placeholder="…or paste https:// or /api/files/ URL"
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111] text-xs"
            />
          </div>

          <div>
            <label className={labelCls}>Inline gallery · up to 20 images</label>
            <input
              data-testid="article-edit-gallery-file"
              type="file"
              accept="image/*"
              multiple
              onChange={handleGalleryUpload}
              disabled={uploadingGallery}
              className="mt-2 text-xs"
            />
            {uploadingGallery && <span className="ml-2 label-eyebrow text-[#A87900]">Uploading…</span>}
            {form.gallery.length > 0 && (
              <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
                {form.gallery.map((url, idx) => (
                  <div
                    key={`${url}-${idx}`}
                    className="relative group"
                    data-testid={`article-edit-gallery-item-${idx}`}
                  >
                    <img
                      src={resolveImg(url)}
                      alt=""
                      className="w-full aspect-square object-cover border border-[#E5E0D8]"
                    />
                    <button
                      type="button"
                      onClick={() => removeFromGallery(idx)}
                      data-testid={`article-edit-gallery-remove-${idx}`}
                      className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-[#D32F2F] hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Video URL · YouTube/Vimeo embed or /api/files/ upload</label>
            <Input
              data-testid="article-edit-video"
              value={form.video_url}
              onChange={set("video_url")}
              placeholder="https://youtube.com/… or /api/files/…"
              className={inputCls}
            />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-none border-[#E5E0D8] text-[#555] hover:text-[#111] px-5 py-3 h-auto label-eyebrow"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="article-edit-submit"
              disabled={saving}
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5 mr-1 rotate-45 hidden" />
              {saving ? "Saving…" : isPublished ? "Save & republish" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
